import { api } from './client'
import type { ProductLot } from './product-lot-api'
import type { RefundChoice } from './sales-return-api'
import type { CustomerDisplaySyncRequest } from '@/lib/schemas/customer-display-event-schemas'

export type SalesOrderStatus =
    | 'draft'
    | 'new'
    | 'awaiting_packaging'
    | 'confirmed'
    | 'stock_out'
    | 'ready_to_deliver'
    | 'in_delivery'
    | 'delivered'
    | 'delivery_failed'
    | 'returned'
    | 'cancelled'

export type PaymentStatus = 'unpaid' | 'partial' | 'paid'

/**
 * Giá trị hợp lệ của tham số lọc `payment_status`: một trạng thái đơn lẻ, hoặc
 * tổ hợp ngăn bởi dấu phẩy mà API OR lại với nhau.
 *
 * `'unpaid,partial'` được đặt tên riêng vì đây là định nghĩa "còn nợ" của hệ
 * thống: công nợ phải thu gồm cả đơn khách mới trả một phần. Lọc thiếu `partial`
 * là ra danh sách nhỏ hơn số tiền đang hiển thị (FD-376).
 */
export const PAYMENT_STATUS_OWING = 'unpaid,partial'

export type PaymentStatusFilter = PaymentStatus | typeof PAYMENT_STATUS_OWING

export type PrintJobStatus = 'queued' | 'printing' | 'printed' | 'failed'

export interface PrintJob {
    id: string
    sales_order_id: string
    doc_type: string
    status: PrintJobStatus
    attempts: number
    last_error?: string | null
    printed_at?: string | null
    created_at: string
    updated_at: string
}

export interface SalesOrderItem {
    id: string
    sales_order_id: string
    product_id: string
    product_name: string
    product_sku?: string
    // Đơn vị bán (gói/kg/hộp) — BE gán từ product khi tạo item. Optional để
    // không vỡ order cũ chưa có unit.
    unit?: string
    quantity: number
    unit_price: number
    discount_percent: number
    subtotal: number
    /** Dòng đã tách giá vật tư khỏi giá SP (SP price_includes_material). */
    material_price_split?: boolean
    /** Vị trí dòng trên bảng hàng hóa — dãy số CHUNG với vật tư của đơn, để
     *  hiển thị lại đúng thứ tự xen kẽ người bán đã nhập. Đơn cũ = 0. */
    sort_order?: number
    weight_value?: number
    weight_unit?: string
    /** Mã/tên/ĐVT gốc của dòng hoá đơn KiotViet (chỉ đơn KiotViet, M-145). */
    kiotviet_product_code?: string
    kiotviet_product_name?: string
    kiotviet_unit?: string
    /** Dòng đang gắn SP tạm "Chưa nối KiotViet" — phải nối SP trước xuất kho. */
    kiotviet_unlinked?: boolean
}

/** Kết quả nối dòng KiotViet vào SP ERP (M-145). */
export interface KiotVietLinkResult {
    kiotviet_product_code: string
    /** Số dòng đã nối (gồm dòng đang chọn + dòng "Chưa nối" cùng mã ở đơn khác). */
    linked_items: number
    orders: number
    previous_product_code?: string
    previous_owner_id?: string
}

// ---- Xuất kho: gợi ý lô + payload chọn lô ----
export interface StockOutSuggestionAllocation {
    product_lot: ProductLot
    quantity: number
}
export interface StockOutSuggestionItem {
    order_item_id: string
    product_id: string
    product_name: string
    unit: string
    quantity: number
    allocations: StockOutSuggestionAllocation[]
}
// Khớp JSON entity.PackagingLot BE nhúng trong suggestion (khác DTO
// /packaging-lots: dùng `code`/`quantity_available`, không phải lot_code/qty_remaining).
export interface StockOutPackagingLot {
    id: string
    code: string
    packaging_material_id: string
    warehouse_id: string
    quantity_available: number
    position_code?: string | null
    position_name?: string | null
}
export interface StockOutMaterialSuggestionAllocation {
    packaging_lot: StockOutPackagingLot
    quantity: number
}
export interface StockOutMaterialSuggestion {
    packaging_material_id: string
    material_name: string
    material_sku: string
    unit: string
    tracking_mode: 'lot' | 'quantity'
    needed_qty: number
    allocations: StockOutMaterialSuggestionAllocation[]
}
export interface StockOutSuggestion {
    warehouse_id: string
    items: StockOutSuggestionItem[]
    materials: StockOutMaterialSuggestion[]
}
export interface StockOutAllocationInput {
    product_lot_id: string
    quantity: number
}
export interface StockOutItemInput {
    order_item_id: string
    allocations: StockOutAllocationInput[]
}
export interface StockOutMaterialAllocationInput {
    packaging_lot_id: string
    quantity: number
}
export interface StockOutMaterialItemInput {
    packaging_material_id: string
    allocations: StockOutMaterialAllocationInput[]
}

export interface SalesOrder {
    id: string
    order_code: string
    source_channel: string
    customer_id: string
    customer_name: string
    customer_phone: string
    shipping_address: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    status: SalesOrderStatus
    total_amount: number
    vat_percent?: number | null
    vat_amount?: number | null
    // Phụ thu hóa đơn KiotViet — đã gộp trong total_amount, chỉ để hiển thị tách
    // dòng "Phụ thu" ở chi tiết đơn. NULL/0 = không có.
    kiotviet_surcharge?: number | null
    payment_status: PaymentStatus
    paid_amount: number
    /** M-44: tổng giá trị hàng khách đã trả sau giao. */
    returned_amount?: number
    /** M-44: tổng phí trả hàng bên bán giữ lại từ tiền khách đã trả. */
    return_fee_amount?: number
    // Credit trả trước đã cấn vào đơn lúc tạo (tick "Trừ vào số dư khách").
    // Chỉ có trên response tạo đơn; dùng để hiện toast xác nhận.
    applied_credit?: number
    created_by_id: string
    created_by_name?: string
    payment_method?: string
    // 'counter' = đơn "Bán ngay" tại quầy, khách nhận hàng tại chỗ.
    settlement_intent?: 'cash' | 'bank_transfer' | 'credit' | 'counter'
    // Đơn "Bán ngay" POS: tự nhảy delivered sau khi bán; vẫn hủy được ở delivered.
    is_quick_sale?: boolean
    discount_percent?: number
    driver_assignee_id?: string
    delivered_at?: string
    delivery_note?: string
    product_notes?: string
    /** Mã đơn NV tự điền để đối chiếu với sổ ghi tay. Free-text, có thể trùng. */
    internal_order_code?: string
    /** Số kiện (thùng) của đơn; hãng in lên vận đơn, nhãn thùng in mỗi kiện một tờ. Mặc định 1. */
    package_count?: number
    shipping_carrier_id?: string
    shipping_carrier_name?: string
    /**
     * Mirror của shipping_carriers.is_external join-resolved trên order.
     * FE dùng để render các button carrier-mark-* (kế toán reconcile) khi
     * đơn giao bởi carrier ngoài.
     */
    shipping_carrier_is_external?: boolean
    /** Người đặt của đơn web / Mini App (chỉ có ở chi tiết đơn); customer_* là người nhận. */
    orderer_name?: string | null
    orderer_phone?: string | null
    /** Dịch vụ Viettel Post khách chọn khi đặt web / Mini App (chỉ có ở chi tiết đơn). */
    customer_shipping_service_code?: string | null
    customer_shipping_service_name?: string | null
    /** Đơn quà web (§14.21) — chỉ GET /sales-orders/:id, vắng với đơn khác. */
    gift?: OrderGift
    shipping_fee?: number
    shipping_fee_payer?: 'customer' | 'company'
    // null = "không chọn" (ẩn trên nhãn thùng + chi tiết); behaves like false.
    collect_cod?: boolean | null
    sla_deadline_at?: string
    /** Thời điểm bắt đầu bắn cảnh báo "sắp trễ" (deadline - warning_minutes). */
    sla_warning_at?: string
    sla_breached: boolean
    /** Có giá trị khi đơn đang nợ lô; null/absent = đủ hàng. */
    lot_debt?: SalesOrderLotDebtSummary | null
    delivery_trip_id?: string
    delivery_vehicle?: DeliveryVehicle
    pod_photo_url?: string
    pod_photo_captured_at?: string
    created_at: string
    updated_at: string
    items?: SalesOrderItem[]
    /** Cờ "Xuất hóa đơn điện tử": true = tự tạo bản nháp khi giao thành công. */
    issue_einvoice?: boolean
    /**
     * Status thô của dòng einvoice, server suy ra khi lấy danh sách. Rỗng =
     * chưa có hóa đơn. Dùng EInvoiceStatusBadge để gộp về nhóm nghiệp vụ thay
     * vì so sánh chuỗi rải rác.
     */
    einvoice_status?: string
    /**
     * Thông tin người mua công ty để xuất HĐĐT, join khi lấy chi tiết đơn. Có
     * mặt khi khách lấy hóa đơn công ty; vắng = khách lẻ. Form sửa đơn prefill.
     */
    invoice_buyer?: InvoiceBuyer
}

/** Người mua gắn với đơn để xuất hóa đơn điện tử (đọc từ chi tiết đơn). */
export interface InvoiceBuyer {
    id: string
    sales_order_id: string
    /** 'company' | 'consumer'; dòng cũ trước FD-367 trả chuỗi rỗng ⇒ công ty. */
    buyer_type: string
    buyer_name: string
    tax_code: string
    address: string
    email: string
    created_at: string
    updated_at: string
}

/**
 * Thông tin người mua công ty gửi lên khi tạo/sửa đơn. Mọi field optional
 * (chỉ nhắc, không bắt buộc). Create: bỏ trống/undefined = khách lẻ. Update:
 * undefined = giữ nguyên; {} = xóa (bỏ tick); có field = upsert.
 */
export interface InvoiceBuyerInput {
    /** Bỏ trống ⇒ hóa đơn công ty (hành vi cũ). 'consumer' ⇒ chỉ gửi buyer_name. */
    buyer_type?: 'company' | 'consumer'
    buyer_name?: string
    tax_code?: string
    address?: string
    email?: string
}

/** Vehicle of the order's delivery trip, joined on the detail fetch. */
export interface DeliveryVehicle {
    id: string
    plate_number: string
    name: string
    gps_provider?: string
    gps_imei?: string
}

export interface SalesOrderStatusLog {
    id: string
    sales_order_id: string
    from_status?: string
    to_status: string
    changed_by_id: string
    changed_by_name: string
    note?: string
    created_at: string
}

export type PaymentMethodChangeTarget = 'cash' | 'bank_transfer'

/**
 * Một lần đổi hình thức (M-152): một khoản thu trên đơn (customer_payment_id)
 * hoặc cả một phiếu thu (receipt_id).
 */
export interface PaymentMethodChange {
    id: string
    customer_payment_id?: string
    receipt_id?: string
    from_method: PaymentMethodChangeTarget
    to_method: PaymentMethodChangeTarget
    changed_by_name: string
    created_at: string
}

export interface SalesOrderPayment {
    id: string
    sales_order_id: string
    amount: number
    payment_method: string
    reference?: string
    notes?: string
    created_by_name: string
    collected_by_driver_id?: string
    collected_by_driver_name?: string
    receipt_id?: string
    created_at: string
    /**
     * Mã lỗi vì sao khoản này KHÔNG đổi được Tiền mặt ↔ Chuyển khoản (M-152);
     * vắng = đổi được. Cùng mã với lỗi PATCH .../method.
     */
    method_change_block?: string
    /** Các lần đã đổi hình thức, cũ nhất trước. */
    method_changes?: PaymentMethodChange[]
}

export interface SalesOrderNotification {
    id: string
    sales_order_id?: string
    receiver_user_id: string
    type: string
    payload: unknown
    is_read: boolean
    created_at: string
}

export interface CreateOrderItemInput {
    product_id: string
    quantity: number
    unit_price: number
    discount_percent?: number
    /** Dòng đã tách giá vật tư (lưu trạng thái nút Tách/Gộp). */
    material_price_split?: boolean
    /** Vị trí dòng trên bảng hàng hóa — dãy số CHUNG với packaging_items nên
     *  sản phẩm và vật tư xen kẽ được (xem `@/lib/order-line-order`). Bỏ trống
     *  (POS) → BE xếp sản phẩm trước, vật tư sau như trước đây. */
    sort_order?: number
}

// Phase 4 — packaging draft item gửi kèm khi tạo đơn.
// source: 'bom_autofill' (hệ thống gợi ý theo BOM) | 'manual' (sale tự thêm).
export interface CreatePackagingItemInput {
    packaging_material_id: string
    quantity: number
    source?: 'bom_autofill' | 'manual'
    /** Đơn giá vật tư (giá vật tư). 0 = không tính tiền. */
    unit_price?: number
    /** Chiết khấu dòng (%) của vật tư (người bán nhập). */
    discount_percent?: number
    /** Sản phẩm mà dòng vật tư thuộc về (để hiển thị inline). */
    product_id?: string
    /** Vị trí dòng đơn (0-based, theo mảng items) mà dòng vật tư thuộc về.
     *  BE map index → order_item_id. Bỏ trống cho vật tư đính-kèm/thêm tay. */
    order_item_index?: number
    /** Vị trí dòng trên bảng hàng hóa — chung dãy số với items. Vật tư định mức
     *  gửi luôn sort_order của dòng sản phẩm cha. */
    sort_order?: number
}

export interface CreateOrderRequest {
    source_channel: string
    customer_id: string
    customer_name: string
    customer_phone: string
    shipping_address: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    product_notes?: string
    /** Mã đơn NV tự điền để đối chiếu với sổ ghi tay. Free-text, có thể trùng. */
    internal_order_code?: string
    shipping_carrier_id?: string
    shipping_fee?: number
    shipping_fee_payer?: 'customer' | 'company'
    // Whether to collect payment from the customer on delivery (COD). Tri-state:
    // true = thu, false = không thu, null = "không chọn" (ẩn nhãn/chi tiết).
    // Omitted → BE defaults to true (POS/mobile paths stay collectible).
    collect_cod?: boolean | null
    // Khách đang có số dư trả trước → cấn luôn vào đơn này (tick "Trừ vào số dư
    // khách"). BE áp min(credit, giá trị đơn) thành khoản thanh toán credit.
    apply_credit?: boolean
    // Tạo đơn ở trạng thái nháp (draft): không auto-confirm. "Tạo đơn nhanh" bật.
    save_as_draft?: boolean
    discount_percent?: number
    // Thuế GTGT người bán chỉnh trên form (2 chiều % ↔ tiền). Gửi cả hai để BE
    // snapshot đúng số tiền (tránh lệch làm tròn). Bỏ trống → BE dùng VAT global.
    vat_percent?: number
    vat_amount?: number
    items?: CreateOrderItemInput[]
    // Phase 4 — vật tư bao bì sale đã chốt cho đơn (autofill từ BOM, có thể
    // edit tự do). BE lưu vào sales_order_packaging_draft, Confirm dùng draft.
    packaging_items?: CreatePackagingItemInput[]
    // Khi kho thiếu hàng, true cho phép BE tạo đơn ở AwaitingPackaging (chờ đội
    // đóng gói bổ sung) thay vì reject 409. Đủ hàng thì auto-confirm bình thường.
    // FE dùng flag này để retry sau khi cashier confirm popup "Kho thiếu, tạo
    // đơn chờ đóng gói?".
    allow_awaiting?: boolean
    // Cổng "Xuất hóa đơn điện tử": true = gửi MISA khi đơn giao thành công.
    issue_einvoice?: boolean
    // Người mua công ty (HĐĐT). undefined/bỏ trống = khách lẻ. Có field = tạo dòng.
    invoice_buyer?: InvoiceBuyerInput
    // POS: lô SP thu ngân chọn/quét tại quầy (khoá theo product_id vì POS gộp
    // trùng SP về 1 dòng). Chỉ QuickSale / CreateDeliveryOrder dùng; rỗng → FIFO.
    pos_lot_allocations?: PosLotAllocationInput[]
    // POS: lô vật tư thu ngân chọn. Rỗng → FIFO.
    pos_material_lots?: StockOutMaterialItemInput[]
}

export interface PosLotAllocationInput {
    product_id: string
    allocations: StockOutAllocationInput[]
}

// Giỏ hàng POS (chưa tạo đơn) → BE gợi ý lô FIFO, cùng shape StockOutSuggestion.
export interface StockOutPreviewRequest {
    items: { product_id: string; quantity: number; discount_percent?: number }[]
    materials: { packaging_material_id: string; quantity: number }[]
}

// Backend trả về khi POST /sales-orders 409 INSUFFICIENT_STOCK: liệt kê SP
// thiếu để FE render bảng popup "Tạo đơn chờ đóng gói?".
export interface StockShortfallItem {
    product_id: string
    product_name: string
    available: number
    requested: number
}

// Response từ GET /sales-orders/:id/stock-check cho đơn AwaitingPackaging.
export interface StockCheckResponse {
    ready: boolean
    shortfalls: StockShortfallItem[]
}

// Một dòng trong popup gán tài xế của admin.
export interface AssignableDriver {
    id: string
    /** Phân biệt hai tài khoản trùng `full_name`. */
    username: string
    full_name: string
    active_order_count: number
}

export interface QuickSaleRequest extends CreateOrderRequest {
    payment_method: 'cash' | 'bank_transfer'
    /** Phần khách đưa dư ghi vào số dư khách (chỉ khách có tên). */
    overpay_credit?: number
    reference?: string
    display_user_id?: string
    /** M-175: tiền khách thực đưa theo từng hình thức (tiền mặt + chuyển
     *  khoản). Thiếu → nợ của đơn (cần khách có tên); `[]` = chưa trả. Bỏ
     *  trống = thu đủ bằng `payment_method` như client cũ. */
    payments?: QuickSalePayment[]
}

export interface QuickSalePayment {
    method: 'cash' | 'bank_transfer'
    amount: number
}

export interface UpdateOrderRequest {
    customer_name?: string
    customer_phone?: string
    shipping_address?: string
    shipping_province?: string
    shipping_province_code?: string
    shipping_ward?: string
    shipping_ward_code?: string
    product_notes?: string | null
    internal_order_code?: string | null
    shipping_carrier_id?: string
    shipping_fee?: number
    // '' explicitly clears the payer ("không chọn"); omitting leaves it unchanged.
    shipping_fee_payer?: 'customer' | 'company' | ''
    // null = "không chọn" (clear/hide); omitted leaves it unchanged.
    collect_cod?: boolean | null
    vat_percent?: number
    vat_amount?: number
    items?: CreateOrderItemInput[]
    // Phase 4 — sale sửa lại draft vật tư kèm đơn. undefined = giữ draft
    // hiện tại; [] = xoá draft; array có item = thay toàn bộ.
    packaging_items?: CreatePackagingItemInput[]
    // Cổng "Xuất hóa đơn điện tử" (patch, undefined = không đổi).
    issue_einvoice?: boolean
    // Người mua công ty (HĐĐT). undefined = giữ nguyên; {} = xóa (bỏ tick);
    // có field = upsert.
    invoice_buyer?: InvoiceBuyerInput
}

export interface ListOrdersParams {
    page?: number
    per_page?: number
    // Một hoặc nhiều trạng thái ngăn bởi dấu phẩy (OR); màn danh sách đơn gửi
    // mặc định mọi trạng thái trừ "Đã hủy".
    status?: SalesOrderStatus | (string & {})
    // Nhiều giá trị ngăn bởi dấu phẩy thì OR với nhau. 'unpaid,partial' =
    // "đơn còn nợ" — đúng tập mà ô "Chưa thanh toán" trên dashboard cộng tiền,
    // vì công nợ gồm cả đơn trả một phần (FD-376).
    payment_status?: PaymentStatusFilter | (string & {})
    // Nhóm nghiệp vụ của hóa đơn điện tử, không phải status kỹ thuật.
    einvoice_status?:
        | 'none'
        | 'awaiting'
        | 'draft'
        | 'pushed'
        | 'processing'
        | 'rejected'
        | 'failed'
        | 'issued'
        | 'manual_issued'
        | 'skipped'
        | 'needs_action'
        | (string & {})
    source_channel?: string
    // exclude_source_channel loại một kênh khỏi kết quả (source_channel != X).
    // Màn "Đơn chờ nhận" của tài xế truyền 'kiotviet' để bỏ đơn bán quầy KiotViet.
    exclude_source_channel?: string
    // shop_channel: chỉ đơn đặt qua website / Zalo Mini App thật (có dòng
    // storefront_orders) — khác source_channel 'online' gồm cả đơn tạo tay.
    shop_channel?: 'web' | 'zalo_mini_app'
    // exclude_quick_sale='1' bỏ đơn "Bán ngay" tại quầy (khách đã cầm hàng về)
    // khỏi pool nhận đơn của tài xế.
    exclude_quick_sale?: '1'
    // quick_sale='1' chỉ lấy đơn "Bán ngay" tại quầy — bảng nhắc hóa đơn (M-166).
    // Không dùng cùng exclude_quick_sale (API trả 400).
    quick_sale?: '1'
    // search_address='1': search khớp cả địa chỉ giao (màn "Đơn chờ nhận").
    search_address?: '1'
    settlement_intent?:
        'cash' | 'bank_transfer' | 'credit' | 'counter' | (string & {})
    // has_lot_debt='1' chỉ lấy đơn CÒN nợ lô (FD-372) — worklist của kho.
    has_lot_debt?: '1'
    // kiotviet_unlinked='1' chỉ lấy đơn KiotViet còn dòng chưa nối SP (M-145).
    kiotviet_unlinked?: '1'
    customer_id?: string
    // shipping_carrier_id lọc theo đơn vị vận chuyển gán trên đơn (M-15).
    shipping_carrier_id?: string
    // sla_state lọc theo hạn xử lý, khớp badge SLA (sla-utils.ts getSLAState).
    sla_state?: 'overdue' | 'warning' | 'on_time' | (string & {})
    // carrier_tracked chỉ giữ đơn hãng ngoài đã thực sự cầm hàng (webhook báo
    // lấy hàng), không dựa vào đơn vị vận chuyển ghi trên đơn.
    carrier_tracked?: '1'
    // carrier_return tách đơn VTP đang giữ chờ quyết định (505) / đang chở về
    // (502, chưa về HTX — 504) khỏi danh sách giao quá hạn (M-15 r3).
    carrier_return?: 'excluded' | 'awaiting_decision' | 'returning'
    // split_vtp_returning='1': in_delivery trong status không gồm đơn VTP đang
    // hoàn về; nhóm đó lấy bằng status `vtp_returning` (M-86).
    split_vtp_returning?: '1'
    // created_by='me' giới hạn về đơn do chính người đang đăng nhập tạo. Server
    // tự phân giải 'me' theo token; UUID tường minh chỉ có tác dụng với người
    // có quyền xem chéo, còn lại bị ép về chính họ.
    created_by?: string
    search?: string
    // with_totals='1': meta.totals = tổng tiền/đã trả của cả tập đơn khớp lọc.
    with_totals?: '1'
    date_from?: string
    date_to?: string
    // delivered_from/delivered_to lọc theo NGÀY GIAO (`delivered_at`), khác
    // date_from/date_to vốn lọc theo ngày tạo. Đơn chưa giao tự rơi khỏi kết
    // quả. Dùng khi cần danh sách khớp con số của báo cáo — doanh thu và công
    // nợ đều tính trên mốc giao hàng.
    delivered_from?: string
    delivered_to?: string
    // payable=true chỉ lấy đơn còn nhận thanh toán (chưa huỷ + còn phải thu > 0)
    // — dùng cho ô chọn đơn ở màn đối soát.
    payable?: boolean
}

export interface RecordPaymentInput {
    amount: number
    method: string
    reference?: string
}

export interface ConfirmOrderPayload {
    note?: string
    // Phase 4 — vật tư bao bì user submit. Thường để rỗng và BE đọc draft
    // (đã lưu lúc Create). Chỉ truyền nếu muốn override draft tại Confirm step.
    packaging_items?: { packaging_material_id: string; quantity: number }[]
    // Cho phép thiếu stock vật tư (warning bypass). Mọi shortage giờ chỉ là warning.
    accept_packaging_shortage?: boolean
}

// Vật tư đã trừ ở Confirm step (group theo lot). FE ConfirmReturn popup
// prefill list + trang chi tiết đơn hiển thị.
export interface PackagingDeductedItem {
    packaging_material_id: string
    material_sku: string
    material_name: string
    material_unit: string
    warehouse_id: string
    packaging_lot_id: string
    lot_code: string
    deducted_qty: number
}

// SP đã trừ kho ở Confirm step (group theo lot). Mirror PackagingDeductedItem.
export interface InventoryDeductedItem {
    product_id: string
    product_sku: string
    product_name: string
    inventory_lot_id: string
    lot_code: string
    deducted_qty: number
    product_unit?: string
    /**
     * 'virtual' = dòng NỢ LÔ (FD-372): hàng đã giao nhưng kho chưa nhập bù lô
     * thật. Lô ảo không có phả hệ để truy xuất và KHÔNG hoàn kho được.
     */
    lot_status?: string
    /** M-38: kệ nhập mặc định cho hàng tốt khi xác nhận hoàn. */
    suggested_position_id?: string
    /** Nhãn "Mã — Tên" của suggested_position_id. */
    suggested_position_label?: string
    /** M-38: kệ nhập mặc định cho phần hàng lỗi (kệ hàng lỗi đã cấu hình). */
    suggested_defect_position_id?: string
    /** Nhãn "Mã — Tên" của suggested_defect_position_id. */
    suggested_defect_position_label?: string
    /** M-38: vị trí hiện tại của lô — chọn đúng kệ này mới có "Tạo lô mới". */
    current_position_id?: string
}

/**
 * Dòng lô ảo (FD-372): hàng đã giao nhưng kho chưa nhập bù lô thật. Lô ảo không
 * có phả hệ để truy xuất và KHÔNG hoàn kho được — mọi màn đụng tới dòng xuất kho
 * đều phải hỏi qua đây thay vì so chuỗi 'virtual' tại chỗ.
 */
export function isVirtualLot(item: { lot_status?: string }): boolean {
    return item.lot_status === 'virtual'
}

/**
 * Tồn đọng nợ lô toàn hệ thống, cho ô "Đang nợ lô" ở dashboard kho (FD-372).
 * `orders` đếm theo ĐƠN, không theo dòng nợ.
 */
export interface LotDebtOverview {
    orders: number
    /**
     * Số LOẠI sản phẩm đang nợ. Cố ý không có tổng số lượng: mỗi sản phẩm một
     * đơn vị (Kg, Gói…), cộng lại thành một số là vô nghĩa.
     */
    products: number
    oldest_open_at: string
}

/** Một mặt hàng đang nợ của đơn, kèm ĐƠN VỊ của chính nó. */
export interface LotDebtLine {
    product_name: string
    remaining: number
    unit: string
}

/**
 * Tóm tắt nợ lô của một đơn (FD-372).
 *
 * Cố ý KHÔNG có tổng số lượng: một đơn nợ 1 Kg chè rời và 3 Gói chè 12,5g thì
 * tổng "4" vô nghĩa. Chỗ nói về NHIỀU sản phẩm thì đếm số loại (items.length);
 * chỉ chỗ nói về MỘT sản phẩm mới hiện số lượng.
 */
export interface SalesOrderLotDebtSummary {
    items: LotDebtLine[]
    oldest_open_at: string
}

// Phase 4 — packaging draft response (cho FE Confirm flow đọc trước khi confirm).
export interface PackagingDraftItem {
    id: string
    sales_order_id: string
    packaging_material_id: string
    quantity: number
    source: 'bom_autofill' | 'manual'
    /** Đơn giá vật tư (giá vật tư) snapshot lúc lên đơn. */
    unit_price: number
    /** Chiết khấu dòng (%) của vật tư. */
    discount_percent: number
    /** Sản phẩm mà dòng vật tư thuộc về. */
    product_id: string
    /** Dòng đơn (order_item) mà dòng vật tư thuộc về. null cho order-level. */
    order_item_id?: string | null
    material_sku: string
    material_name: string
    material_unit: string
    /** SUM(packaging_lots.qty_remaining) cho material — FE so qty để detect shortage. */
    available: number
    /** Vị trí dòng trên bảng hàng hóa — chung dãy số với sort_order của dòng
     *  sản phẩm (xem `@/lib/order-line-order`). Đơn cũ = 0. */
    sort_order?: number
    created_at: string
    updated_at: string
}

export type StockOutNoteStatus = 'pending' | 'confirmed' | 'cancelled'

export interface StockOutNote {
    id: string
    code: string
    sales_order_id: string
    warehouse_id: string
    status: StockOutNoteStatus
    notes?: string
    /** Packing photo-evidence URLs attached at confirmation. */
    photo_paths: string[]
    created_by_id: string
    confirmed_by_id?: string
    created_at: string
    confirmed_at?: string
}

export interface ConfirmStockOutNoteRequest {
    note?: string
    /** Upload URLs of packing evidence photos (at least one required). */
    photo_paths: string[]
}

export interface DriverDeliverySummary {
    delivered: number
    in_delivery: number
    on_time: number
    sla_breached: number
}

export interface MyDeliverySummaryParams {
    date_from?: string
    date_to?: string
}

/** Đơn quà web (hợp đồng v3 §14.21); chỉ chi tiết đơn có, vắng với đơn khác. */
export interface OrderGift {
    message: string | null
    hide_price: boolean
    /** Người đặt chuyển khoản cả ship — tài xế / hãng không thu cước người nhận. */
    shipping_prepaid: boolean
    shipping_fee: number
    /** Ship đã ghi trên các phiếu thu của đơn. */
    shipping_paid: number
}

export interface PaymentQRData {
    qr_url: string
    bank_account: string
    bank_name: string
    bank_holder: string
    amount: number
    content: string
}

/** `meta.totals` của danh sách đơn khi gửi `with_totals=1`. */
export interface SalesOrderListTotals {
    total_amount: number
    paid_amount: number
}

export const salesOrderApi = {
    list: (params?: ListOrdersParams) =>
        api.get<SalesOrder[], SalesOrderListTotals>('/sales-orders', {
            params,
        }),
    getById: (id: string) => api.get<SalesOrder>(`/sales-orders/${id}`),
    /**
     * Xuất toàn bộ đơn tạo trong ngày ra Excel. Trả nguyên AxiosResponse để
     * caller đọc Content-Disposition lấy tên file server đặt.
     */
    exportDailyExcel: (
        date: string,
        opts?: {
            createdByIds?: string[]
            paymentMethods?: string[]
            sourceChannels?: string[]
        },
    ) => {
        // paid_only cố định true (UI đã bỏ checkbox); rỗng thì không gửi filter.
        const params: Record<string, string | boolean> = {
            date,
            paid_only: true,
        }
        if (opts?.sourceChannels && opts.sourceChannels.length > 0) {
            params.source_channels = opts.sourceChannels.join(',')
        }
        if (opts?.createdByIds && opts.createdByIds.length > 0) {
            params.created_by_ids = opts.createdByIds.join(',')
        }
        if (opts?.paymentMethods && opts.paymentMethods.length > 0) {
            params.payment_methods = opts.paymentMethods.join(',')
        }
        return api.getBlob('/sales-orders/export/daily-excel', { params })
    },
    /**
     * Resolve a free-form code (DH... order code or PXK-... stock-out code)
     * to the underlying sales order. Used by the QR/barcode scanner so users
     * can type the printed code on a slip if the camera fails.
     */
    lookupByCode: (code: string) =>
        api.get<SalesOrder>(`/sales-orders/lookup`, {
            params: { code },
        }),
    /**
     * Sửa mã đơn nội bộ. Cho phép ở MỌI trạng thái đơn (kể cả đã giao / đã huỷ) vì
     * đây là mã đối chiếu với hệ thống ngoài, có thể được cấp/đổi sau khi đơn đã đi
     * hết vòng đời. Đổi lại: BE chỉ cho ĐÚNG NGƯỜI TẠO ĐƠN sửa (403 nếu không phải).
     * Gửi chuỗi rỗng = xoá mã.
     */
    updateInternalCode: (id: string, internalOrderCode: string) =>
        api.patch<SalesOrder>(`/sales-orders/${id}/internal-code`, {
            internal_order_code: internalOrderCode,
        }),
    create: (data: CreateOrderRequest) =>
        api.post<SalesOrder>('/sales-orders', data),
    quickCreate: (data: QuickSaleRequest) =>
        api.post<SalesOrder>('/sales-orders/quick', data),
    /**
     * Create a deliver-later (giao sau) order from the POS. Goes through
     * create → confirm → stock-out with no payment (COD); the order lands at
     * stock_out + unpaid so a driver can pick it up immediately.
     */
    createDelivery: (data: CreateOrderRequest) =>
        api.post<SalesOrder>('/sales-orders/delivery', data),
    displaySync: (data: CustomerDisplaySyncRequest) =>
        api.post<{ success: boolean }>('/customer-display/sync', data, {
            signal: AbortSignal.timeout(10_000),
        }),
    displayState: (signal?: AbortSignal) =>
        api.get<unknown>('/customer-display/state', {
            signal: signal
                ? AbortSignal.any([signal, AbortSignal.timeout(10_000)])
                : AbortSignal.timeout(10_000),
        }),
    update: (id: string, data: UpdateOrderRequest) =>
        api.put<SalesOrder>(`/sales-orders/${id}`, data),

    // Workflow actions
    /**
     * Kiểm tra sẵn sàng Confirm cho đơn AwaitingPackaging. Trả ready + shortfalls[].
     * Read-only, có thể poll F5. Backend reject 400 nếu đơn không ở trạng thái
     * awaiting_packaging.
     */
    checkStock: (id: string) =>
        api.get<StockCheckResponse>(`/sales-orders/${id}/stock-check`),
    confirm: (id: string, data?: ConfirmOrderPayload) =>
        api.post<SalesOrder>(`/sales-orders/${id}/confirm`, data ?? {}),
    /** Nối dòng KiotViet chưa nối (hoặc nối lại) vào SP ERP (M-145). */
    linkKiotVietProduct: (orderId: string, itemId: string, productId: string) =>
        api.post<KiotVietLinkResult>(
            `/sales-orders/${orderId}/items/${itemId}/link-kiotviet-product`,
            { product_id: productId },
        ),
    getStockOutSuggestion: (id: string) =>
        api.get<StockOutSuggestion>(`/sales-orders/${id}/stock-out/suggestion`),
    // POS: gợi ý lô cho giỏ hàng trước khi tạo đơn.
    stockOutPreview: (data: StockOutPreviewRequest) =>
        api.post<StockOutSuggestion>('/sales-orders/stock-out/preview', data),
    stockOut: (
        id: string,
        data?: {
            note?: string
            items?: StockOutItemInput[]
            material_items?: StockOutMaterialItemInput[]
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/stock-out`, data ?? {}),
    assignDriver: (id: string, data: { driver_id: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/assign-driver`, data),
    /**
     * Danh sách tài xế cho popup gán đơn của admin. `active_order_count` = số
     * đơn tài xế đang giữ (ready_to_deliver / in_delivery); 0 = đang rảnh.
     * Tài xế bận vẫn gán được — một chuyến chở nhiều đơn — nên đây là thông
     * tin tham khảo, không phải bộ lọc.
     */
    assignableDrivers: () =>
        api.get<AssignableDriver[]>('/sales-orders/assignable-drivers'),
    pickupOrder: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/pickup-order`, data ?? {}),
    startDelivery: (
        id: string,
        data: {
            note?: string
            vehicle_id?: string
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/start-delivery`, data),
    confirmDelivery: (
        id: string,
        data: {
            note?: string
            // Settlement on delivery: 'cash' records driver-collected cash (feeds
            // the cash ledger), 'bank_transfer' defers to the webhook, 'credit'
            // leaves the debt. Omitted records nothing.
            method?: 'cash' | 'bank_transfer' | 'credit'
            cash_amount?: number
            // With 'bank_transfer': the customer paid into the driver's own
            // account (M-133) — the driver holds it like cash.
            transfer_to_driver?: boolean
            // Driver also collected the order's carrier fee in cash
            // ("thu cước hộ"); amount comes from the order's shipping_fee.
            collect_shipping_fee?: boolean
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/confirm-delivery`, data),
    failDelivery: (id: string, data: { note: string }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/fail-delivery`, data),
    confirmReturn: (
        id: string,
        data?: {
            note?: string
            packaging_restore_items?: {
                packaging_lot_id: string
                quantity: number
            }[]
            /** FD-397: SL hàng lỗi theo lô — phần lỗi vào kệ hàng lỗi. */
            defect_items?: {
                product_lot_id: string
                quantity: number
            }[]
            /** M-38: kệ nhập cho phần hàng tốt theo lô. */
            restock_positions?: {
                product_lot_id: string
                position_id: string
                new_lot?: boolean
            }[]
            /** M-38: kệ nhập cho phần hàng lỗi theo lô. */
            defect_positions?: {
                product_lot_id: string
                position_id: string
                new_lot?: boolean
            }[]
            /** M-44: lựa chọn hoàn tiền khách đã trả. */
            refund?: RefundChoice
        },
    ) => api.post<SalesOrder>(`/sales-orders/${id}/confirm-return`, data ?? {}),
    cancel: (id: string, data: { note: string; refund?: RefundChoice }) =>
        api.post<SalesOrder>(`/sales-orders/${id}/cancel`, data),
    // Huỷ đơn đã xác nhận rồi tạo đơn NHÁP mới sao chép nó. Không có body: BE
    // lấy toàn bộ dữ liệu từ chính đơn cũ. Response = ĐƠN MỚI.
    recreate: (id: string) =>
        api.post<SalesOrder>(`/sales-orders/${id}/recreate`, {}),
    // Sao chép đơn (M-187): tạo đơn NHÁP mới từ đơn có sẵn, đơn gốc giữ nguyên.
    // Không có body. Response = ĐƠN MỚI.
    duplicate: (id: string) =>
        api.post<SalesOrder>(`/sales-orders/${id}/duplicate`, {}),

    // Carrier reconcile — kế toán mark delivery status thay driver cho đơn
    // giao bởi carrier bên ngoài (shipping_carrier_is_external=true). Payload
    // tối giản: chỉ note, không lat/lng, không vehicle, không COD method.
    carrierMarkReadyToDeliver: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-ready-to-deliver`,
            data ?? {},
        ),
    carrierMarkInDelivery: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-in-delivery`,
            data ?? {},
        ),
    carrierMarkDelivered: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-delivered`,
            data ?? {},
        ),
    carrierMarkDeliveryFailed: (id: string, data?: { note?: string }) =>
        api.post<SalesOrder>(
            `/sales-orders/${id}/carrier-mark-delivery-failed`,
            data ?? {},
        ),

    // Payments
    recordPayments: (id: string, data: { payments: RecordPaymentInput[] }) =>
        api.post<SalesOrderPayment[]>(`/sales-orders/${id}/payments`, data),
    listPayments: (id: string) =>
        api.get<SalesOrderPayment[]>(`/sales-orders/${id}/payments`),
    changePaymentMethod: (
        id: string,
        paymentId: string,
        method: PaymentMethodChangeTarget,
    ) =>
        api.patch<SalesOrderPayment>(
            `/sales-orders/${id}/payments/${paymentId}/method`,
            { method },
        ),

    // Timeline
    timeline: (id: string) =>
        api.get<SalesOrderStatusLog[]>(`/sales-orders/${id}/timeline`),

    // Phase 4 — packaging draft của 1 đơn (sale đã chốt lúc tạo). FE Confirm
    // flow đọc để hiển thị + dùng làm packaging_items.
    getPackagingDraft: (id: string) =>
        api.get<PackagingDraftItem[]>(`/sales-orders/${id}/packaging-draft`),
    getPackagingDeducted: (id: string) =>
        api.get<PackagingDeductedItem[]>(
            `/sales-orders/${id}/packaging-deducted`,
        ),
    getInventoryDeducted: (id: string) =>
        api.get<InventoryDeductedItem[]>(
            `/sales-orders/${id}/inventory-deducted`,
        ),

    // Ô "Đang nợ lô" ở dashboard kho (FD-372).
    getLotDebtOverview: () =>
        api.get<LotDebtOverview>('/sales-orders/lot-debts/overview'),

    // Notifications
    listNotifications: (params?: { page?: number; per_page?: number }) =>
        api.get<SalesOrderNotification[]>('/notifications', { params }),
    markNotificationRead: (id: string) =>
        api.put('/notifications/' + id + '/read'),
    markAllNotificationsRead: () => api.put('/notifications/read-all'),
    unreadNotificationCount: () =>
        api.get<{ count: number }>('/notifications/unread-count'),

    // Push subscription
    subscribePush: (data: { endpoint: string; p256dh: string; auth: string }) =>
        api.post('/push/subscribe', data),
    unsubscribePush: (endpoint: string) =>
        api.post('/push/unsubscribe', { endpoint }),

    // Payment QR
    getPaymentQR: (id: string) =>
        api.get<PaymentQRData>(`/sales-orders/${id}/payment-qr`),

    // Driver self-scoped delivery performance (no reports.read required)
    myDeliverySummary: (params?: MyDeliverySummaryParams) =>
        api.get<DriverDeliverySummary>('/sales-orders/my-delivery-summary', {
            params,
        }),

    // Self-scoped revenue summary for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myOrdersSummary: (params?: MyDeliverySummaryParams) =>
        api.get<MyRevenueSummary>('/sales-orders/summary/mine', { params }),

    // Self-scoped revenue time-series for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myRevenueByTime: (params?: RevenueByTimeParams) =>
        api.get<RevenueByTimeEntry[]>('/sales-orders/revenue/by-time', {
            params: params as Record<string, string | undefined>,
        }),

    // Self-scoped order counts by status for the authenticated sales rep.
    // Gated sales_orders.read — does not require reports.read.
    myOrdersByStatus: (params?: MyDeliverySummaryParams) =>
        api.get<MyOrdersByStatusResponse>(
            '/sales-orders/summary/mine/by-status',
            {
                params: params as Record<string, string | undefined>,
            },
        ),

    // Self-scoped daily delivery counts for the authenticated driver.
    // Gated sales_orders.read — does not require reports.read.
    myDeliveriesByTime: (params?: MyDeliverySummaryParams) =>
        api.get<DeliveryByTimeEntry[]>('/sales-orders/deliveries/by-time', {
            params: params as Record<string, string | undefined>,
        }),
}

export interface MyRevenueSummary {
    total_revenue: number
    total_orders: number
    average_order_value: number
    paid_amount: number
    unpaid_amount: number
    unique_customers: number
    date_from: string
    date_to: string
}

export interface RevenueByTimeParams {
    group_by?: 'day' | 'week' | 'month' | 'year'
    date_from?: string
    date_to?: string
}

export interface RevenueByTimeEntry {
    period: string
    revenue: number
    order_count: number
}

export interface MyOrdersByStatusResponse {
    total_orders: number
    by_status: Record<string, number>
    delivery_rate: number
}

export interface DeliveryByTimeEntry {
    period: string
    count: number
}

export interface ListStockOutNotesParams {
    status?: StockOutNoteStatus
    page?: number
    per_page?: number
}

export const stockOutNoteApi = {
    /**
     * List stock-out notes with optional status filter + pagination.
     * Gated stock_out.confirm.
     */
    list: (params?: ListStockOutNotesParams) =>
        // Cast matches the pre-existing pattern for typed params in this codebase.
        api.get<StockOutNote[]>('/stock-out-notes', {
            params: params as Record<
                string,
                string | number | boolean | undefined
            >,
        }),
    /**
     * Resolve a PXK-... code (typed or scanned from packing slip) to its
     * stock-out note so the warehouse confirm screen can load by URL code.
     */
    getByCode: (code: string) =>
        api.get<StockOutNote>(
            `/stock-out-notes/by-code/${encodeURIComponent(code)}`,
        ),
    confirm: (id: string, data: ConfirmStockOutNoteRequest) =>
        api.put<StockOutNote>(`/stock-out-notes/${id}/confirm`, data),
}
